import * as _ from 'lodash';

import { TableItem } from './item';
import { TableGroup } from './group';
import { TableRow } from './row';

import { TableButtonData, TableDefaultEvent, TableButtonDropdownData, TableButtonBasicData, TableBooleanFunction, TableStringFunction } from '../table.interface';
import { ButtonMode, ButtonSize } from '../../button/button.component';

export class TableButton {
  constructor(
    private _buttonData: TableButtonData,
    private _tableItem: TableItem,
    private _tableRow: TableRow,
    private _tableGroup?: TableGroup
  ) {
    this._setEvent();
    this._setLabel();
    this._setVisible();
    this._setDisabled();
    this._setList();
  }

  private _list: Array<TableButton> = [];

  private _event: TableDefaultEvent;

  private _label: string = null;
  private _title: string = null;
  private _visible = true;
  private _disabled = true;
  private _has_visible_items = false;

  public get type(): string { return this._buttonData.type || 'button'; }

  public get label(): string { return this._label; }

  public get title(): string { return this._title; }

  public get icon(): string { return this._buttonData.icon || (this.type == 'dropdown' ? 'more_vert' : null); }

  public get icon_mode(): string { return this._buttonData.icon_mode || 'md'; }

  public get color(): string { return this._buttonData.color || 'primary'; }

  public get mode(): ButtonMode { return this._buttonData.mode || 'icon'; }

  public get size(): ButtonSize { return this._buttonData.size || 'sm'; }

  public get visible(): boolean { return !!this._visible; }

  public get disabled(): boolean { return !!this._disabled; }

  public get has_visible_items(): boolean { return this._has_visible_items; }

  public get action(): Function {
    return () => {
      (<TableButtonBasicData>this._buttonData).action(this._event);
      if (this._tableRow) { this._tableRow.update(); }
    };
  }

  public get list(): Array<TableButton> {
    return this._list;
  }

  public update() {
    this._setEvent();
    this._setLabel();
    this._setVisible();
    this._setDisabled();
    this._updateList();
  }

  private _setEvent() {
    this._event = {
      table_row: this._tableRow,
      table_item: this._tableItem,
      data: this._tableRow ? this._tableRow.getData() : (this._tableGroup ? this._tableGroup.getData() : null)
    };
  }

  private _setList() {
    if (this.type == 'dropdown' && (<TableButtonDropdownData>this._buttonData).list) {
      this._list = (<TableButtonDropdownData>this._buttonData).list.map((subitem) => {
        const button_data: TableButtonBasicData = {
          type: 'button',
          label: subitem.label,
          icon: subitem.icon,
          title: subitem.title,
          visible: subitem.visible,
          disabled: subitem.disabled,
          action: subitem.action
        };
        return new TableButton(button_data, this._tableItem, this._tableRow);
      });
    } else {
      this._list = [];
    }

    this._setHasVisibleItems();
  }

  private _setLabel() {
    let label = null;
    let title = null;

    if (_.isFunction(this._buttonData.label)) {
      label = (<TableStringFunction>this._buttonData.label)(this._event);
    } else if (_.isString(this._buttonData.label)) {
      label = (<string>this._buttonData.label);
    } else {
      label = null;
    }

    if (_.isFunction(this._buttonData.title)) {
      title = (<TableStringFunction>this._buttonData.title)(this._event);
    } else if (_.isString(this._buttonData.title)) {
      title = (<string>this._buttonData.title);
    } else {
      title = null;
    }

    this._label = label;
    this._title = title || label;
  }

  private _setVisible() {
    if (_.isFunction(this._buttonData.visible)) {
      this._visible = (<TableBooleanFunction>this._buttonData.visible)(this._event);
    } else if (_.isBoolean(this._buttonData.visible)) {
      this._visible = (<boolean>this._buttonData.visible);
    } else {
      this._visible = true;
    }
  }

  private _setDisabled() {
    if (_.isFunction(this._buttonData.disabled)) {
      this._disabled = (<TableBooleanFunction>this._buttonData.disabled)(this._event);
    } else if (_.isBoolean(this._buttonData.disabled)) {
      this._disabled = (<boolean>this._buttonData.disabled);
    } else {
      this._disabled = false;
    }
  }

  private _updateList() {
    if (!this._list) { return; }

    this._list.forEach((button) => button.update());
    this._setHasVisibleItems();
  }

  private _setHasVisibleItems() {
    this._has_visible_items = this.list.filter((i) => i.visible).length > 0;
  }
}
